import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";

const card = "bg-white rounded-[16px] p-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100";

export function AutoAccept() {
  const [d, setD] = useState(null);
  const [pct, setPct] = useState("");
  useEffect(() => { api.get("/agency/auto-accept").then(({ data }) => { setD(data); setPct(data.sample_pct); }).catch(() => {}); }, []);
  if (!d) return null;
  const savePct = async () => {
    try { const { data } = await api.put("/agency/auto-accept/sample", { pct: Number(pct) }); setD({ ...d, sample_pct: data.sample_pct }); toast.success("Amostra guardada"); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const toggle = async (r, enabled) => {
    try {
      await api.put("/agency/auto-accept", { key: r.key, enabled });
      setD({ ...d, rows: d.rows.map((x) => (x.key === r.key ? { ...x, enabled } : x)) });
      toast.success(enabled ? `Aceitação automática ligada: ${r.name}` : `Aceitação automática desligada: ${r.name}`);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <div className={card} data-testid="auto-accept">
      <h2 className="font-heading text-xl text-ec-green">Aceitação automática</h2>
      <p className="text-sm text-slate-600 mt-1 max-w-2xl">
        Com o interruptor ligado, um documento lido com confiança alta, com os campos obrigatórios e sem avisos fica “Verificado automaticamente” e não entra em “Para rever”.
        {` ${d.sample_pct}%`} destes vão ao acaso para “Para rever” como amostra. Só pode ligar os tipos permitidos pela plataforma.
      </p>
      <div className="mt-3 flex flex-wrap items-center gap-2 text-sm text-slate-700" data-testid="auto-accept-sample">
        Amostra <input type="number" min={d.min_sample_pct} max={100} value={pct} onChange={(e) => setPct(e.target.value)} className="w-20 h-9 rounded-md border border-slate-200 px-2" data-testid="auto-accept-sample-input" /> %
        <button onClick={savePct} disabled={String(pct) === String(d.sample_pct)} className="h-9 px-3 rounded-lg bg-ec-green text-white text-sm font-semibold disabled:opacity-50" data-testid="auto-accept-sample-save">Guardar</button>
        <span className="text-xs text-slate-500">mínimo {d.min_sample_pct}% (regra da plataforma); pode aumentar, não diminuir</span>
      </div>
      <div className="mt-4 divide-y divide-slate-100">
        {d.rows.map((r) => (
          <div key={r.key} className="flex items-center justify-between gap-3 py-2.5" data-testid={`auto-accept-row-${r.key}`}>
            <div className="min-w-0">
              <div className="text-[15px] text-slate-800 truncate">{r.name}</div>
              {r.one_click && <div className="text-xs text-slate-500">Documento com valores: confirmação sempre com um clique</div>}
              {!r.one_click && !r.allowed && <div className="text-xs text-slate-500" data-testid={`auto-accept-blocked-${r.key}`}>Não permitido pela plataforma</div>}
              {r.id_card && <div className="text-xs text-slate-500">Cada cartão novo é confirmado uma vez por uma pessoa</div>}
            </div>
            <Switch checked={r.enabled} disabled={r.one_click || (!r.allowed && !r.enabled)} onCheckedChange={(v) => toggle(r, v)} data-testid={`auto-accept-switch-${r.key}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function ReadQuality() {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/platform/read-quality").then(({ data }) => setD(data)).catch(() => {}); }, []);
  if (!d) return null;
  return (
    <div className={card} data-testid="admin-read-quality">
      <h2 className="font-heading text-xl text-ec-green">Qualidade da leitura</h2>
      <p className="text-sm text-slate-600 mt-1" data-testid="admin-read-quality-total">
        {d.total ? `${d.ok} de ${d.total} leituras confirmadas sem correção (${d.pct}%).` : "Ainda sem leituras revistas."} Use esta percentagem para decidir quando ligar a aceitação automática.
      </p>
      {d.rows.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-500"><th className="py-1.5 pr-3 font-medium">Tipo de documento</th><th className="py-1.5 pr-3 font-medium text-right">Revistas</th><th className="py-1.5 pr-3 font-medium text-right">Sem correção</th><th className="py-1.5 font-medium text-right">%</th></tr></thead>
            <tbody className="divide-y divide-slate-100">
              {d.rows.map((r) => [
                <tr key={r.key} onClick={() => setOpen(open === r.key ? null : r.key)} className="cursor-pointer hover:bg-slate-50" data-testid={`admin-rq-${r.key}`}>
                  <td className="py-1.5 pr-3 text-slate-800">{r.name}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{r.total}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{r.ok}</td>
                  <td className={`py-1.5 text-right tabular-nums font-semibold ${r.pct >= 95 ? "text-green-700" : r.pct < 80 ? "text-red-700" : "text-slate-700"}`}>{r.pct}%</td>
                </tr>,
                open === r.key && r.agencies.map((a) => (
                  <tr key={`${r.key}-${a.agency}`} className="bg-slate-50/60 text-xs" data-testid={`admin-rq-${r.key}-agency`}>
                    <td className="py-1 pr-3 pl-4 text-slate-600">{a.agency}</td>
                    <td className="py-1 pr-3 text-right tabular-nums">{a.total}</td>
                    <td className="py-1 pr-3 text-right tabular-nums">{a.ok}</td>
                    <td className="py-1 text-right tabular-nums">{a.pct}%</td>
                  </tr>
                )),
              ])}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
